import { useState, useEffect } from 'react';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '../../utils/timezone';
import { Shield, Building2, Package, Plus, X, Truck, Phone, Mail, Globe, Leaf, Pencil, Trash2, ShieldCheck, FileCheck, Lock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import { Tabs } from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import sprayService from '../../services/spray';
import apiService from '../../services/api';
import orchardService from '../../services/orchard';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { ApiError } from '../../types';
import type { SprayOrgAdvisor, AdvisorPermission, ChemicalInventoryLocation, SupplierOrg } from '../../types/spray';
import type { Farm } from '../../types/orchard';
import CredentialStatusBadge from '../../components/spray/CredentialStatusBadge';
import SituationGroupsTab from '../../components/spray/SituationGroupsTab';
import {
  listCredentialTypes,
  createCredentialType,
  updateCredentialType,
  deactivateCredentialType,
  getReadinessReport,
  createCredential,
  updateCredential,
  type SprayCredentialType,
  type OperatorReadinessRow,
} from '../../services/sprayCredential';

type AdminTab = 'advisors' | 'locations' | 'suppliers' | 'situations' | 'credential_types' | 'operator_credentials';

// ============================================================================
// Advisors tab
// ============================================================================

// Shape returned by `GET /users/lookup?email=` — the cross-org user lookup the
// grant form resolves an email address through.
interface LookedUpUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  organisation_id: number;
  organisation_name: string;
}

const ALL_PERMISSIONS: AdvisorPermission[] = ['view_spray', 'create_spray', 'edit_spray', 'manage_chemicals'];
const PERMISSION_LABELS: Record<AdvisorPermission, string> = {
  view_spray:      'View sprays',
  create_spray:    'Create sprays',
  edit_spray:      'Edit sprays',
  manage_chemicals: 'Manage chemicals',
};

function AdvisorsTab() {
  const timezone = useOrgTimezone();
  const [advisors, setAdvisors] = useState<SprayOrgAdvisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGrant, setShowGrant] = useState(false);
  const [grantForm, setGrantForm] = useState({
    email: '',
    advisor_user_id: '',
    advisor_org_id: '',
    permissions: ['view_spray', 'create_spray', 'edit_spray'] as AdvisorPermission[],
    expires_at: '',
    notes: '',
  });
  const [searchedUser, setSearchedUser] = useState<LookedUpUser | null>(null);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [granting, setGranting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  // A failed lookup must drop the ids too, not just the card — otherwise the
  // previous hit stays armed behind a new, unresolved email address.
  const clearLookup = (message: string) => {
    setSearchedUser(null);
    setGrantForm(prev => ({ ...prev, advisor_user_id: '', advisor_org_id: '' }));
    setGrantError(message);
  };

  const loadAdvisors = async () => {
    setLoading(true);
    const res = await sprayService.getAdvisors();
    if (res.success && res.data) setAdvisors(res.data as SprayOrgAdvisor[]);
    setLoading(false);
  };

  useEffect(() => { loadAdvisors(); }, []);

  const handleRevoke = async (id: number) => {
    await sprayService.revokeAdvisorAccess(id);
    loadAdvisors();
  };

  const togglePerm = (perm: AdvisorPermission) => {
    setGrantForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleLookupUser = async () => {
    if (!grantForm.email) return;
    setLookingUp(true);
    setGrantError(null);
    try {
      const res = await apiService.get<LookedUpUser>(
        `/users/lookup?email=${encodeURIComponent(grantForm.email)}`
      );
      if (res.success && res.data) {
        setSearchedUser(res.data);
        setGrantForm(prev => ({
          ...prev,
          advisor_user_id: String(res.data!.id),
          advisor_org_id: String(res.data!.organisation_id),
        }));
      } else {
        clearLookup('No active user found with that email address.');
      }
    } catch (err) {
      // The interceptor rejects with a shaped ApiError, so the server's own
      // message comes through — a missing seat reads as a missing seat rather
      // than as "no such user", which is how the missing route used to read.
      clearLookup((err as ApiError)?.message || 'Could not look up user');
    } finally {
      setLookingUp(false);
    }
  };

  const handleGrant = async () => {
    if (!grantForm.advisor_user_id || !grantForm.advisor_org_id) {
      setGrantError('Look up a user first');
      return;
    }
    setGranting(true);
    setGrantError(null);
    const res = await sprayService.grantAdvisorAccess({
      advisor_user_id: Number(grantForm.advisor_user_id),
      advisor_org_id: Number(grantForm.advisor_org_id),
      permissions: grantForm.permissions,
      expires_at: grantForm.expires_at || null,
      notes: grantForm.notes || undefined,
    });
    if (res.success) {
      setShowGrant(false);
      setSearchedUser(null);
      loadAdvisors();
    } else {
      setGrantError((res as any).error?.message || 'Failed to grant access');
    }
    setGranting(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-neutral-500">
          External advisors from other organisations can view and manage your sprays.
        </p>
        <Button variant="primary" size="sm" onClick={() => setShowGrant(true)}>
          <Plus className="h-4 w-4" />
          Grant Access
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
        </div>
      ) : advisors.length === 0 ? (
        <div className="text-center py-10 text-neutral-400 text-sm">No advisors have been granted access</div>
      ) : (
        <div className="space-y-3">
          {advisors.map(advisor => (
            <div key={advisor.id} className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-neutral-800">
                    {[advisor.advisor_first_name, advisor.advisor_last_name].filter(Boolean).join(' ') || advisor.advisor_email}
                  </p>
                  <p className="text-sm text-neutral-500">{advisor.advisor_email} · {advisor.advisor_org_name}</p>
                  {advisor.expires_at && (
                    <p className="text-xs text-neutral-400 mt-1">
                      Expires: {formatDateInTz(advisor.expires_at, timezone)}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    advisor.is_active ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                  }`}>
                    {advisor.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => handleRevoke(advisor.id)}
                    className="text-neutral-400 hover:text-red-500 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {advisor.permissions.map(perm => (
                  <span key={perm} className="bg-neutral-100 text-neutral-600 text-xs px-2 py-0.5 rounded">
                    {PERMISSION_LABELS[perm] || perm}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showGrant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full my-4">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-base font-semibold text-neutral-800">Grant Advisor Access</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Advisor email</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={grantForm.email}
                    onChange={e => setGrantForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="advisor@theirorg.com"
                    className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLookupUser}
                    disabled={lookingUp || !grantForm.email}
                  >
                    {lookingUp ? 'Looking up...' : 'Look up'}
                  </Button>
                </div>
                {searchedUser && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <p className="text-green-800 font-medium">
                      {[searchedUser.first_name, searchedUser.last_name].filter(Boolean).join(' ') || searchedUser.email}
                    </p>
                    <p className="text-green-600 text-xs">{searchedUser.organisation_name}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-2">Permissions</label>
                <div className="space-y-1.5">
                  {ALL_PERMISSIONS.map(perm => (
                    <label key={perm} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={grantForm.permissions.includes(perm)}
                        onChange={() => togglePerm(perm)}
                        className="rounded"
                      />
                      <span className="text-sm text-neutral-700">{PERMISSION_LABELS[perm]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Expires (optional)</label>
                <input type="date" value={grantForm.expires_at}
                  onChange={e => setGrantForm(prev => ({ ...prev, expires_at: e.target.value }))}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              </div>

              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
                <textarea value={grantForm.notes} rows={2}
                  onChange={e => setGrantForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>

              {grantError && <p className="text-sm text-red-600">{grantError}</p>}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowGrant(false); setGrantError(null); setSearchedUser(null); }}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleGrant} disabled={granting || !searchedUser}>
                {granting ? 'Granting...' : 'Grant Access'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Inventory locations tab
// ============================================================================

function LocationsTab() {
  const [locations, setLocations] = useState<ChemicalInventoryLocation[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', description: '', farm_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLocations = async () => {
    setLoading(true);
    const res = await sprayService.getInventoryLocations();
    if (res.success && res.data) setLocations(res.data as ChemicalInventoryLocation[]);
    setLoading(false);
  };

  useEffect(() => {
    loadLocations();
    orchardService.getFarms(1, 100).then(res => {
      if (res.success && res.data) setFarms((res.data as any).farms ?? []);
    });
  }, []);

  const openAdd = () => {
    setForm({ name: '', description: '', farm_id: '' });
    setEditingId(null);
    setShowAdd(true);
    setError(null);
  };

  const openEdit = (loc: ChemicalInventoryLocation) => {
    setForm({ name: loc.name, description: loc.description || '', farm_id: loc.farm_id ? String(loc.farm_id) : '' });
    setEditingId(loc.id);
    setShowAdd(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.name) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name,
      description: form.description || undefined,
      farm_id: form.farm_id ? Number(form.farm_id) : null,
    };
    let res: any;
    if (editingId) {
      res = await sprayService.updateInventoryLocation(editingId, payload);
    } else {
      res = await sprayService.createInventoryLocation(payload);
    }
    if (res.success) {
      setShowAdd(false);
      loadLocations();
    } else {
      setError(res.error?.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-neutral-500">Named storage areas for chemical inventory (e.g. sheds, stores). Assign each to a farm.</p>
        <Button variant="primary" size="sm" onClick={openAdd}>
          <Plus className="h-4 w-4" />
          Add Location
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-neutral-100 rounded-lg animate-pulse" />)}
        </div>
      ) : locations.length === 0 ? (
        <div className="text-center py-10 text-neutral-400 text-sm">No inventory locations yet</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
          {locations.map(loc => (
            <div key={loc.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-medium text-neutral-800">{loc.name}</p>
                {loc.farm_name && <p className="text-xs text-primary-600 font-medium">{loc.farm_name}</p>}
                {loc.description && <p className="text-sm text-neutral-400">{loc.description}</p>}
                {loc.chemical_count != null && (
                  <p className="text-xs text-neutral-400">{loc.chemical_count} product{loc.chemical_count !== 1 ? 's' : ''}</p>
                )}
              </div>
              <button
                onClick={() => openEdit(loc)}
                className="text-xs text-neutral-400 hover:text-primary-600 transition-colors px-2 py-1 rounded hover:bg-primary-50"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-base font-semibold text-neutral-800">
              {editingId ? 'Edit Location' : 'Add Inventory Location'}
            </h3>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Farm *</label>
              <select value={form.farm_id} onChange={e => setForm(prev => ({ ...prev, farm_id: e.target.value }))}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select farm...</option>
                {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Chemical Shed A"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2} className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

function SprayAdminSettings() {
  // All hooks first — guards come after. Per HHjsFrontEnd CLAUDE.md,
  // admin/super_admin RBAC roles do NOT bypass module license checks.
  const { hasAccess, loading: licenseLoading } = useModuleLicense('spray', 'admin');
  const [activeTab, setActiveTab] = useState<AdminTab>('advisors');

  const tabs: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: 'advisors',             label: 'External Advisors',    icon: <Building2 className="h-4 w-4" />   },
    { key: 'locations',            label: 'Inventory Locations',  icon: <Package className="h-4 w-4" />     },
    { key: 'situations',           label: 'Spray Situations',     icon: <Leaf className="h-4 w-4" />        },
    { key: 'suppliers',            label: 'Suppliers',            icon: <Truck className="h-4 w-4" />       },
    { key: 'credential_types',     label: 'Credential Types',     icon: <FileCheck className="h-4 w-4" />   },
    { key: 'operator_credentials', label: 'Operator Credentials', icon: <ShieldCheck className="h-4 w-4" /> },
  ];

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Spray Admin license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Spray module settings — credential types, situations, inventory locations,
            external advisors, and operator credential administration — require an
            active <span className="font-medium">Spray Admin</span> licence seat.
          </p>
          <p className="text-xs text-neutral-400">
            Your organisation admin can purchase a seat via the Subscriptions page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Spray Admin"
        subtitle="Manage advisors, inventory, credentials and more"
        icon={<Shield className="h-6 w-6 text-white" />}
      />
      <SprayNav />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} className="mb-6" />

        {/* Tab content */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {activeTab === 'advisors'             && <AdvisorsTab />}
          {activeTab === 'locations'            && <LocationsTab />}
          {activeTab === 'situations'           && <SituationGroupsTab />}
          {activeTab === 'suppliers'            && <SuppliersTab />}
          {activeTab === 'credential_types'     && <CredentialTypesTab />}
          {activeTab === 'operator_credentials' && <OperatorCredentialsTab />}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// SUPPLIERS TAB
// ============================================================================

interface SupplierFormData {
  name: string;
  contact_email: string;
  po_email: string;
  contact_phone: string;
  website: string;
  address_line1: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

function emptySupplierForm(): SupplierFormData {
  return { name: '', contact_email: '', po_email: '', contact_phone: '', website: '', address_line1: '', city: '', state: '', postcode: '', country: 'Australia' };
}

function SuppliersTab() {
  const [suppliers, setSuppliers] = useState<SupplierOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SupplierOrg | null>(null);
  const [form, setForm] = useState<SupplierFormData>(emptySupplierForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function loadSuppliers() {
    setLoading(true);
    const res = await sprayService.searchSuppliers();
    if (res.success && res.data) setSuppliers(res.data);
    setLoading(false);
  }

  useEffect(() => { loadSuppliers(); }, []);

  function openAdd() {
    setEditing(null);
    setForm(emptySupplierForm());
    setSaveError(null);
    setShowModal(true);
  }

  function openEdit(s: SupplierOrg) {
    setEditing(s);
    setForm({
      name: s.name,
      contact_email: s.contact_email ?? '',
      po_email: s.po_email ?? '',
      contact_phone: s.contact_phone ?? '',
      website: s.website ?? '',
      address_line1: s.address_line1 ?? '',
      city: s.city ?? '',
      state: s.state ?? '',
      postcode: s.postcode ?? '',
      country: s.country ?? 'Australia',
    });
    setSaveError(null);
    setShowModal(true);
  }

  const f = (field: keyof SupplierFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setSaveError('Name is required'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        // Update org contact fields + ensure supplier type
        await apiService.put(`/organisations/${editing.id}`, {
          ...form,
          stakeholder_types: ['supplier'],
        });
      } else {
        // Create new org + tag as supplier
        const res: { success: boolean; data?: { organisation?: { id: number } } } =
          await apiService.post('/organisations', { name: form.name.trim(), description: '' });
        if (res.success && res.data?.organisation?.id) {
          await apiService.put(`/organisations/${res.data.organisation.id}`, {
            ...form,
            stakeholder_types: ['supplier'],
          });
        }
      }
      await loadSuppliers();
      setShowModal(false);
    } catch {
      setSaveError('Failed to save supplier. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-neutral-900">Suppliers</h3>
          <p className="text-sm text-neutral-500 mt-0.5">Chemical and supply vendors used in purchase orders</p>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Supplier
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-400 py-4">Loading...</p>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-300 rounded-xl">
          <Truck className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
          <p className="text-sm text-neutral-500">No suppliers registered yet</p>
          <button onClick={openAdd} className="mt-2 text-sm text-indigo-600 hover:underline">Add your first supplier</button>
        </div>
      ) : (
        <div className="space-y-2">
          {suppliers.map(s => (
            <div key={s.id} className="flex items-start justify-between border border-neutral-200 rounded-xl p-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-neutral-900">{s.name}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {s.contact_email && (
                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                      <Mail className="h-3 w-3" /> {s.contact_email}
                    </span>
                  )}
                  {s.po_email && (
                    <span className="flex items-center gap-1 text-xs text-indigo-500">
                      <Mail className="h-3 w-3" /> PO: {s.po_email}
                    </span>
                  )}
                  {s.contact_phone && (
                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                      <Phone className="h-3 w-3" /> {s.contact_phone}
                    </span>
                  )}
                  {s.website && (
                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                      <Globe className="h-3 w-3" /> {s.website}
                    </span>
                  )}
                </div>
                {(s.city || s.state) && (
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {[s.address_line1, s.city, s.state, s.postcode].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => openEdit(s)}
                className="text-xs text-indigo-600 hover:underline ml-4 shrink-0"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <h3 className="text-base font-semibold text-neutral-900">
                {editing ? 'Edit Supplier' : 'Add Supplier'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-3 overflow-y-auto max-h-[70vh]">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Company Name *</label>
                <input type="text" value={form.name} onChange={f('name')} required
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Email</label>
                  <input type="email" value={form.contact_email} onChange={f('contact_email')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Phone</label>
                  <input type="tel" value={form.contact_phone} onChange={f('contact_phone')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Purchase Order Email</label>
                <input type="email" value={form.po_email} onChange={f('po_email')} placeholder="Where POs are sent (defaults to Email above)"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                <p className="text-[11px] text-neutral-400 mt-1">Purchase orders are emailed here. Leave blank to use the general Email address.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Website</label>
                <input type="url" value={form.website} onChange={f('website')} placeholder="https://"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">Address</label>
                <input type="text" value={form.address_line1} onChange={f('address_line1')} placeholder="Street address"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">City</label>
                  <input type="text" value={form.city} onChange={f('city')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">State</label>
                  <input type="text" value={form.state} onChange={f('state')} placeholder="WA"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Postcode</label>
                  <input type="text" value={form.postcode} onChange={f('postcode')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>

              {saveError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" className="flex-1" disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Add Supplier')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CREDENTIAL TYPES TAB
// Org-scoped catalogue of credential types operators can record (ACUP,
// Chemical Users License, etc.). Marked as required or optional.
// ============================================================================

function CredentialTypesTab() {
  const [types, setTypes] = useState<SprayCredentialType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SprayCredentialType | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await listCredentialTypes(true);
    if (res.success) setTypes(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setIsRequired(true);
    setError(null);
    setShowModal(true);
  };

  const openEdit = (t: SprayCredentialType) => {
    setEditing(t);
    setName(t.name);
    setDescription(t.description || '');
    setIsRequired(t.is_required_for_operators);
    setError(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const res = await updateCredentialType(editing.id, {
          name: name.trim(),
          description: description.trim() || null,
          is_required_for_operators: isRequired,
        });
        if (!res.success) throw new Error('Update failed');
      } else {
        const res = await createCredentialType({
          name: name.trim(),
          description: description.trim() || null,
          is_required_for_operators: isRequired,
        });
        if (!res.success) throw new Error('Create failed');
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: SprayCredentialType) => {
    if (t.is_active) {
      if (!confirm(`Deactivate "${t.name}"? Users who already recorded it will keep the record.`)) return;
      await deactivateCredentialType(t.id);
    } else {
      await updateCredentialType(t.id, { is_active: true });
    }
    await load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Credential Types</h2>
          <p className="text-sm text-neutral-600">
            Define the credentials operators in your organisation must hold
            (e.g. ACUP, Chemical Users License, SMARTtrain).
          </p>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="h-4 w-4 mr-1" /> Add type
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : types.length === 0 ? (
        <p className="text-sm text-neutral-500">No credential types defined yet.</p>
      ) : (
        <div className="divide-y divide-neutral-200">
          {types.map(t => (
            <div key={t.id} className="flex items-center justify-between py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${t.is_active ? 'text-neutral-900' : 'text-neutral-400 line-through'}`}>{t.name}</span>
                  {t.is_required_for_operators && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Required</span>
                  )}
                  {!t.is_active && (
                    <span className="text-xs bg-neutral-100 text-neutral-500 px-2 py-0.5 rounded-full">Inactive</span>
                  )}
                </div>
                {t.description && <p className="text-xs text-neutral-500 mt-0.5">{t.description}</p>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} className="p-1.5 text-neutral-500 hover:text-primary-600" aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => toggleActive(t)} className="p-1.5 text-neutral-500 hover:text-rose-600" aria-label="Toggle active">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <header className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit' : 'Add'} credential type</h3>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
            </header>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                       placeholder="e.g. Agricultural Chemical Users Permit (ACUP)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description (optional)</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                          className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} />
                Required for spray operators
              </label>
              {error && <p className="text-sm text-rose-600">{error}</p>}
            </div>
            <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
              <Button onClick={save} isLoading={saving}>Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OPERATOR CREDENTIALS TAB — org-wide readiness report + inline admin entry.
// Admins can record or update an operator's credential on their behalf.
// ============================================================================

function OperatorCredentialsTab() {
  const orgTz = useOrgTimezone();
  const [rows, setRows] = useState<OperatorReadinessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<OperatorReadinessRow | null>(null);
  const [form, setForm] = useState({
    credential_number: '',
    issuer: '',
    issued_date: '',
    expiry_date: '',
    notes: '',
  });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const res = await getReadinessReport();
    if (res.success) setRows(res.data);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, []);

  const openEdit = (row: OperatorReadinessRow) => {
    setEditing(row);
    setForm({
      credential_number: row.credential_number || '',
      issuer: row.issuer || '',
      issued_date: row.issued_date || '',
      expiry_date: row.expiry_date || '',
      notes: '',
    });
    setDocFile(null);
    setError(null);
  };

  const closeModal = () => {
    setEditing(null);
    setError(null);
    setDocFile(null);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      if (editing.credential_id) {
        const res = await updateCredential(editing.credential_id, {
          credential_number: form.credential_number,
          issuer: form.issuer,
          issued_date: form.issued_date || undefined,
          expiry_date: form.expiry_date || undefined,
          notes: form.notes,
          document: docFile,
        });
        if (!res.success) throw new Error('Update failed');
      } else {
        const res = await createCredential({
          credential_type_id: editing.credential_type_id,
          user_id: editing.user_id,
          credential_number: form.credential_number || undefined,
          issuer: form.issuer || undefined,
          issued_date: form.issued_date || undefined,
          expiry_date: form.expiry_date || undefined,
          notes: form.notes || undefined,
          document: docFile,
        });
        if (!res.success) throw new Error('Create failed');
      }
      closeModal();
      setLoading(true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Group by user so the table is scannable.
  const grouped = rows.reduce<Map<number, { user: OperatorReadinessRow; entries: OperatorReadinessRow[] }>>((acc, r) => {
    const existing = acc.get(r.user_id);
    if (existing) existing.entries.push(r);
    else acc.set(r.user_id, { user: r, entries: [r] });
    return acc;
  }, new Map());

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-neutral-900">Operator credentials</h2>
        <p className="text-sm text-neutral-600">
          Status of required credentials for every user holding a Spray Operator or Creator license.
          Use the action column to record or update a credential on an operator's behalf.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-neutral-500">Loading…</p>
      ) : grouped.size === 0 ? (
        <p className="text-sm text-neutral-500">No licensed operators yet.</p>
      ) : (
        <div className="space-y-4">
          {[...grouped.values()].map(({ user, entries }) => (
            <div key={user.user_id} className="border border-neutral-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium text-neutral-900">
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                  </span>
                  <span className="text-xs text-neutral-500 ml-2 capitalize">{user.license_type}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-neutral-500">
                      <th className="text-left py-1">Credential</th>
                      <th className="text-left py-1">Number</th>
                      <th className="text-left py-1">Expires</th>
                      <th className="text-left py-1">Status</th>
                      <th className="text-right py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(e => (
                      <tr key={`${e.user_id}-${e.credential_type_id}`} className="border-t border-neutral-100">
                        <td className="py-1.5">
                          {e.credential_type_name}
                          {e.is_required_for_operators && <span className="text-xs text-amber-600 ml-1">(required)</span>}
                        </td>
                        <td className="py-1.5 text-neutral-700">{e.credential_number || '—'}</td>
                        <td className="py-1.5 text-neutral-700">{e.expiry_date ? formatLocalDateInTz(e.expiry_date, orgTz) : '—'}</td>
                        <td className="py-1.5"><CredentialStatusBadge status={e.status} /></td>
                        <td className="py-1.5 text-right">
                          <button
                            onClick={() => openEdit(e)}
                            className="text-xs text-primary-600 hover:underline whitespace-nowrap"
                          >
                            {e.credential_id ? 'Edit' : 'Record'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <header className="px-5 py-4 border-b border-neutral-200 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">
                  {editing.credential_id ? 'Edit credential' : 'Record credential'}
                </h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {editing.credential_type_name} · {[editing.first_name, editing.last_name].filter(Boolean).join(' ') || editing.username}
                </p>
              </div>
              <button onClick={closeModal} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
            </header>
            <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Credential number</label>
                <input type="text" value={form.credential_number}
                       onChange={e => setForm(f => ({ ...f, credential_number: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Issuer</label>
                <input type="text" value={form.issuer}
                       onChange={e => setForm(f => ({ ...f, issuer: e.target.value }))}
                       className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Issued</label>
                  <input type="date" value={form.issued_date}
                         onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))}
                         className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Expires</label>
                  <input type="date" value={form.expiry_date}
                         onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                         className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
                <textarea value={form.notes} rows={2}
                          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                          className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Document (optional)</label>
                <input type="file" accept="application/pdf,image/*"
                       onChange={e => setDocFile(e.target.files?.[0] || null)}
                       className="w-full text-sm" />
                {editing.credential_id && editing.has_document && !docFile && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Current: {editing.document_original_name || 'document on file'} (upload a new file to replace)
                  </p>
                )}
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
            </div>
            <footer className="px-5 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl flex justify-end gap-2">
              <Button variant="outline" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button onClick={save} isLoading={saving}>Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default SprayAdminSettings;
